import {Component, OnDestroy, OnInit} from '@angular/core';
import {AllFontSelection, FontSelection} from "../../../core/models/theme";
import {BehaviorSubject, combineLatest, Subject, takeUntil} from "rxjs";
import {FormControl, FormGroup} from "@angular/forms";
import {BuildThemeService} from "../../../core/services/customer-theme-services/build-theme.service";
import {DEFAULT_FONTS} from "./default-styles";
import {Customer, StylingSettings} from "../../../core/models";
import {ActivatedRoute} from "@angular/router";
import {FontService} from "../../../core/services/customer-theme-services/font.service";

@Component({
  selector: 'app-customer-styling',
  templateUrl: './customer-styling.component.html',
  styleUrls: ['./customer-styling.component.scss'],
  standalone: false
})
export class CustomerStylingComponent implements OnInit, OnDestroy {
  customer: Customer;

  // We keep both color and text (font) settings inside FormGroups so they can be bound directly to Angular Reactive Forms.
  // This way, child components can update the values directly with minimal data transfer back and forth
  colorSelection: FormGroup = new FormGroup({
    primaryColor: new FormControl<string>('#50504b'), // default primary color from base
    onPrimary: new FormControl<string>('#FFFFFF'),
    secondaryColor: new FormControl<string>('#969691'), // default accent color from base
    onSecondary: new FormControl<string>('#FFFFFF'),
    primaryContainer: new FormControl<string>('#474742'),
    onPrimaryContainer: new FormControl<string>('#000000'),
    secondaryContainer: new FormControl<string>('#e5e2de'),
    onSecondaryContainer: new FormControl<string>('#000000'),
    warnColor: new FormControl<string>('#ff0000'),
    matCardColor: new FormControl<string>('#ffffff'),
    backgroundColor: new FormControl<string>('#f5f5f3'),
    textColor: new FormControl<string>('#32322d'),
    headerTextColor: new FormControl<string>('#32322d'),
    flatButtonColor: new FormControl<string>('#50504b'),
    onFlatButtonColor: new FormControl<string>('#FFFFFF'),
    outline: new FormControl<string>('#71787e'),
    onSecondaryButtonColor: new FormControl<string>('#50504b'),
    selectionColor: new FormControl<string>('#50504b'),
    onSelectionColor: new FormControl<string>('#ffffff')
  })

  fonts: AllFontSelection = {};
  fontSelection: FormGroup = new FormGroup({
    searchHeader: new FormControl<string>(''),
    searchBody: new FormControl<string>(''),
    searchButton: new FormControl<string>(''),
    currentHeaderWeight: new FormControl<number>(600),
    currentBodyWeight: new FormControl<number>(400),
    currentButtonWeight: new FormControl<number>(400)
  })
  currentBodyFont: FontSelection;
  currentHeaderFont: FontSelection;
  currentButtonFont: FontSelection;

  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  loadingTheme: boolean = false;
  destroy$: Subject<boolean> = new Subject<boolean>();

  highLightUpdateThemeBtn: boolean = false;

  constructor(
    private buildThemeService: BuildThemeService,
    private fontService: FontService,
    private route: ActivatedRoute
  ) {
    for (const k of Object.keys(DEFAULT_FONTS)) {
      this.fonts[k] = { ...DEFAULT_FONTS[k] };
    }
    this.route.data.subscribe(data => this.customer = data['customer']);
  }

  ngOnInit(): void {
    this.initStylingFromCustomer();
    this.defaultFonts();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  private initStylingFromCustomer() {
    this.loadingTheme = true;
    this.fontService.getAllFonts().subscribe(fonts => {
      if (!this.customer.settings.styling) {
        this.loadingTheme = false;
        return;
      }

      const styling = this.customer.settings.styling;

      const headerFont = fonts.find(f => f.family === styling.headerFontFamily);
      if (headerFont) {
        this.searchHeader.setValue(styling.headerFontFamily);
        this.currentHeaderFont = headerFont;
      }
      const bodyFont = fonts.find(f => f.family === styling.bodyFontFamily);
      if (bodyFont) {
        this.searchBody.setValue(styling.bodyFontFamily);
        this.currentBodyFont = bodyFont;
      }
      if (styling.buttonFontFamily) {
        const buttonFont = fonts.find(f => f.family === styling.buttonFontFamily);
        if (buttonFont) {
          this.searchButton.setValue(styling.buttonFontFamily);
          this.currentButtonFont = buttonFont;
        }
      }
      this.setAllColors(styling);
      this.loadingTheme = false;
    });
  }

  private defaultFonts(): void {
    if (!this.customer.settings.styling) {
      combineLatest([
        this.fontService.searchAllFonts('Open sans'),
        this.fontService.searchAllFonts('Barlow'),
      ]).pipe(takeUntil(this.destroy$))
        .subscribe(([headerFont, bodyFont]) => {
          this.pickFont('header-font', headerFont[0]);
          this.pickFont('body-font', bodyFont[0]);
        });
    }
  }

  pickFont(type: 'header-font' | 'body-font' | 'button-font', f: FontSelection) {
    this.setFontType(type, f);
    let headerFontLinkElement = document.querySelector('link[id=' + type + ']') as HTMLLinkElement;
    const fontExists = headerFontLinkElement !== null;
    if (!fontExists) {
      headerFontLinkElement = document.createElement('link');
      headerFontLinkElement.id = type;
    }

    headerFontLinkElement.onerror = x => console.log('Unable to load font:', this.getFontType(type).family);
    headerFontLinkElement.href = `https://fonts.googleapis.com/css2?family=${this.getFontType(type).family.replace(/ /g, '+')}:wght@400`;
    headerFontLinkElement.rel = 'stylesheet';

    if (!fontExists) {
      document.head.appendChild(headerFontLinkElement);
    }
  }

  updateTheme(): void {
    this.highLightUpdateThemeBtn = false;
    const styling: StylingSettings = {
      primaryColor: this.primaryColor.value,
      onPrimary: this.onPrimary.value,
      secondaryColor: this.secondaryColor.value,
      onSecondary: this.onSecondary.value,
      primaryContainer: this.primaryContainer.value,
      onPrimaryContainer: this.onPrimaryContainer.value,
      secondaryContainer: this.secondaryContainer.value,
      onSecondaryContainer: this.onSecondaryContainer.value,
      flatButtonColor: this.flatButtonColor.value,
      onFlatButtonColor: this.onFlatButtonColor.value,
      outline: this.outline.value,
      onSecondaryButtonColor: this.onSecondaryButtonColor.value,
      selectionColor: this.selectionColor.value,
      onSelectionColor: this.onSelectionColor.value,

      warnColor: this.warnColor.value,
      matCardColor: this.matCardColor.value,

      headerFontFamily: this.currentHeaderFont.family,
      bodyFontFamily: this.currentBodyFont.family,
      buttonFontFamily: this.currentButtonFont?.family,
      backgroundColor: this.backgroundColor.value,
      textColor: this.textColor.value,
      headerTextColor: this.headerTextColor.value,
      headerWeight: this.currentHeaderWeight.value,
      bodyWeight: this.currentBodyWeight.value,
      buttonWeight: this.currentButtonWeight.value
    }

    this.loading$.next(true);
    this.buildThemeService.setNewTheme(this.customer, styling)
      .subscribe(() => this.loading$.next(false));
  }

  getFontType(type: 'header-font' | 'body-font' | 'button-font'): FontSelection {
    if (type === 'header-font') return this.currentHeaderFont;
    if (type === 'button-font') return this.currentButtonFont;
    return this.currentBodyFont;
  }

  setFontType(type: 'header-font' | 'body-font' | 'button-font', value: FontSelection): void {
    if (type === 'header-font') {
      this.currentHeaderFont = value;
    } else if (type === 'button-font') {
      this.currentButtonFont = value;
    } else {
      this.currentBodyFont = value;
    }
  };

  private setAllColors(styling: StylingSettings): void {
    this.primaryColor.setValue(styling.primaryColor);
    this.warnColor.setValue(styling.warnColor);
    this.onPrimary.setValue(styling.onPrimary);
    this.secondaryColor.setValue(styling.secondaryColor);
    this.onSecondary.setValue(styling.onSecondary);
    this.primaryContainer.setValue(styling.primaryContainer);
    this.onPrimaryContainer.setValue(styling.onPrimaryContainer);
    this.secondaryContainer.setValue(styling.secondaryContainer);
    this.onSecondaryContainer.setValue(styling.onSecondaryContainer);
    this.flatButtonColor.setValue(styling.flatButtonColor ?? styling.primaryColor);
    this.onFlatButtonColor.setValue(styling.onFlatButtonColor ?? styling.onPrimary);
    this.outline.setValue(styling.outline ?? '#71787e');
    this.onSecondaryButtonColor.setValue(styling.onSecondaryButtonColor ?? '#50504b');
    this.selectionColor.setValue(styling.selectionColor ?? styling.primaryColor);
    this.onSelectionColor.setValue(styling.onSelectionColor ?? styling.onPrimary);

    this.backgroundColor.setValue(styling.backgroundColor || '#f5f5f3');
    this.textColor.setValue(styling.textColor || '#32322d');
    this.headerTextColor.setValue(styling.headerTextColor || '#32322d');
    this.matCardColor.setValue(styling.matCardColor || '#ffffff');
    this.currentHeaderWeight.setValue(styling.headerWeight || 600);
    this.currentBodyWeight.setValue(styling.bodyWeight || 400);
    this.currentButtonWeight.setValue(styling.buttonWeight || 400);
    this.pickFont('body-font', this.currentBodyFont);
    this.pickFont('header-font', this.currentHeaderFont);
  }

  resetStyling(): void {
    // Reset the colors to default them (grey)
    this.searchHeader.setValue('Open Sans');
    this.searchBody.setValue('Barlow');
    this.currentBodyWeight.setValue(400);
    this.currentHeaderWeight.setValue(600);
    this.currentButtonWeight.setValue(400);
    this.primaryColor.setValue('#50504b');
    this.onPrimary.setValue('#ffffff');
    this.secondaryColor.setValue('#969691');
    this.onSecondary.setValue('#000000');
    this.warnColor.setValue('#ff0000');
    this.primaryContainer.setValue('#474742');
    this.onPrimaryContainer.setValue('#ffffff');
    this.secondaryContainer.setValue('#e5e2de');
    this.onSecondaryContainer.setValue('#000000');
    this.flatButtonColor.setValue('#50504b');
    this.onFlatButtonColor.setValue('#ffffff');
    this.outline.setValue('#71787e');
    this.onSecondaryButtonColor.setValue('#50504b');
    this.selectionColor.setValue('#50504b');
    this.onSelectionColor.setValue('#ffffff');

    this.matCardColor.setValue('#ffffff');
    this.textColor.setValue('#32322d');
    this.headerTextColor.setValue('#32322d');
    this.backgroundColor.setValue('#f5f5f3');
    this.highLightUpdateThemeBtn = true;
  }

  private get searchHeader(): FormControl {
    return this.fontSelection.get('searchHeader') as FormControl;
  }
  private get searchBody(): FormControl {
    return this.fontSelection.get('searchBody') as FormControl;
  }
  private get searchButton(): FormControl {
    return this.fontSelection.get('searchButton') as FormControl;
  }

  private get currentHeaderWeight(): FormControl {
    return this.fontSelection.get('currentHeaderWeight') as FormControl;
  }
  private get currentBodyWeight(): FormControl {
    return this.fontSelection.get('currentBodyWeight') as FormControl;
  }
  private get currentButtonWeight(): FormControl {
    return this.fontSelection.get('currentButtonWeight') as FormControl;
  }

  private get primaryColor(): FormControl {
    return this.colorSelection.get('primaryColor') as FormControl;
  }
  private get onPrimary(): FormControl {
    return this.colorSelection.get('onPrimary') as FormControl;
  }
  private get secondaryColor(): FormControl {
    return this.colorSelection.get('secondaryColor') as FormControl;
  }
  private get onSecondary(): FormControl {
    return this.colorSelection.get('onSecondary') as FormControl;
  }
  private get primaryContainer(): FormControl {
    return this.colorSelection.get('primaryContainer') as FormControl;
  }
  private get onPrimaryContainer(): FormControl {
    return this.colorSelection.get('onPrimaryContainer') as FormControl;
  }
  private get secondaryContainer(): FormControl {
    return this.colorSelection.get('secondaryContainer') as FormControl;
  }
  private get onSecondaryContainer(): FormControl {
    return this.colorSelection.get('onSecondaryContainer') as FormControl;
  }
  private get flatButtonColor(): FormControl {
    return this.colorSelection.get('flatButtonColor') as FormControl;
  }
  private get onFlatButtonColor(): FormControl {
    return this.colorSelection.get('onFlatButtonColor') as FormControl;
  }
  private get outline(): FormControl {
    return this.colorSelection.get('outline') as FormControl;
  }
  private get onSecondaryButtonColor(): FormControl {
    return this.colorSelection.get('onSecondaryButtonColor') as FormControl;
  }
  private get selectionColor(): FormControl {
    return this.colorSelection.get('selectionColor') as FormControl;
  }
  private get onSelectionColor(): FormControl {
    return this.colorSelection.get('onSelectionColor') as FormControl;
  }


  private get warnColor(): FormControl {
    return this.colorSelection.get('warnColor') as FormControl;
  }
  private get matCardColor(): FormControl {
    return this.colorSelection.get('matCardColor') as FormControl;
  }
  get textColor(): FormControl {
    return this.colorSelection.get('textColor') as FormControl;
  }
  get headerTextColor(): FormControl {
    return this.colorSelection.get('headerTextColor') as FormControl;
  }
  get backgroundColor(): FormControl {
    return this.colorSelection.get('backgroundColor') as FormControl;
  }
}
